import { CONSTANTS } from '../../constants';

/**
 * Every `window.localStorage` accessor can *throw* rather than fail softly:
 * Safari private mode, ITP eviction, storage partitioning and "block all
 * cookies" surface as `SecurityError` or `QuotaExceededError`. A throw from
 * here aborts whichever flow was mid-read or mid-write — `getLocalStorageItem`
 * in particular is called outside the caller's own try in
 * `create-account.api.ts`, so it would reject the whole promise with nothing to
 * catch it.
 *
 * These guards keep storage failures contained. They deliberately do not try to
 * substitute for storage: a browser that refuses to persist has made a
 * decision, and an in-memory stand-in would only paper over it while adding
 * state of its own to keep correct.
 */

export const getLocalStorageItem = (key: string): string | null => {
    try {
        const value = window.localStorage.getItem(key);
        // The spec says getItem returns string | null and never undefined, but
        // this guard exists precisely because we are talking to implementations
        // that do not behave. Anything that is not a string reads as absent.
        return typeof value === 'string' ? value : null;
    } catch {
        return null;
    }
};

export const setLocalStorageItem = (
    key: string,
    value: string,
    keyPrefix: string = '',
    clean: boolean = false
) => {
    try {
        window.localStorage.setItem(keyPrefix + key, value);
        if (clean) {
            deleteAllExpiredStateItems();
        }
    } catch {
        // Storage refused the write. Callers treat the value as absent from
        // here on, which is the same state they would be in had the browser
        // evicted it a moment later.
    }
};

export const removeLocalStorageItem = (key: string): void => {
    try {
        window.localStorage.removeItem(key);
    } catch {
        // Nothing to remove if storage is unreachable.
    }
};

export const deleteAllExpiredStateItems = () => {
    const currentTimeStamp = Date.now();
    const keysToDelete: string[] = [];

    // Enumerating is the only part that still needs its own guard: `length`
    // and `key` are accessors in their own right and throw under the same
    // conditions as the rest, and there is no read-by-key here for
    // `getLocalStorageItem` to cover. Housekeeping is never worth failing a
    // caller over, so an unreadable store simply yields nothing to delete.
    try {
        // Find expired state items
        for (let i = 0; i < window.localStorage.length; i++) {
            const key = window.localStorage.key(i);
            if (key && key.startsWith(CONSTANTS.statePrefixLocalStorage)) {
                const keyWithoutPrefix = key.substring(
                    CONSTANTS.statePrefixLocalStorage.length
                );
                const keyTimeStamp = parseInt(keyWithoutPrefix);
                if (
                    keyTimeStamp <
                    currentTimeStamp - CONSTANTS.stateExpiryTime
                ) {
                    keysToDelete.push(key);
                }
            }
        }
    } catch {
        // Nothing enumerable, so nothing to expire.
    }

    // Delete expired keys. `removeLocalStorageItem` already swallows, so this
    // needs no guard of its own.
    for (const key of keysToDelete) {
        removeLocalStorageItem(key);
    }
};
